import { BookOpen, Compass, Crown, Play, Sparkles, Zap } from 'lucide-react';
import GenerativeLayoutEngine from '../../GenerativeLayoutEngine';
import SystemReasoningPanel from '../../components/SystemReasoningPanel';
import type { IntentClassification, LayoutVariant, UserSignal } from '../../types';
import type { JourneyPath } from '../../../../types/journey';

type GuidesTab = 'journeys' | 'lessons' | 'quick-courses';

interface GuidesIntentWorkspaceProps {
  searchTerm: string;
  selectedFilter: string;
  selectedCategory: string;
  activeTab: GuidesTab;
  journeyPaths: JourneyPath[];
  filteredPaths: JourneyPath[];
  lessons: Array<{ _id: string; title: string }>;
  quickCourses: Array<{ _id: string; title: string }>;
  hasPremiumAccess: boolean;
  onStartJourney: (path: JourneyPath) => void;
  onOpenSubscription: () => void;
  onSwitchTab: (tab: GuidesTab) => void;
  onIntentSuccess: (message: string) => void;
  onIntentFriction: (message: string) => void;
}

const GuidesIntentWorkspace = ({
  searchTerm,
  selectedFilter,
  selectedCategory,
  activeTab,
  journeyPaths,
  filteredPaths,
  lessons,
  quickCourses,
  hasPremiumAccess,
  onStartJourney,
  onOpenSubscription,
  onSwitchTab,
  onIntentSuccess,
  onIntentFriction,
}: GuidesIntentWorkspaceProps) => {
  const freePaths = filteredPaths.filter((path) => !path.isPremium);
  const premiumPaths = filteredPaths.filter((path) => path.isPremium);
  const readyFreePaths = freePaths.filter((path) => path.launchReady);
  const featuredFreePath = readyFreePaths[0] || null;

  const query = [
    searchTerm,
    activeTab === 'journeys' ? 'explore learning paths' : `open ${activeTab}`,
    selectedFilter !== 'all' ? `${selectedFilter} content` : '',
    selectedCategory !== 'all' ? `${selectedCategory} topic` : '',
  ]
    .filter(Boolean)
    .join(' ');

  const signals: UserSignal[] = [
    { type: searchTerm ? 'knowledge-query' : 'route-jump', timestamp: Date.now(), metadata: { activeTab } },
    ...(selectedFilter === 'premium' ? [{ type: 'compare-options' as const, timestamp: Date.now(), metadata: { selectedFilter } }] : []),
  ];

  const resolveVariant = (classification: IntentClassification): LayoutVariant => ({
    id: `guides-${classification.intent.toLowerCase()}`,
    intent: classification.intent,
    slots: {
      hero: () => (
        <div className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">
                <Sparkles className="h-4 w-4" />
                Intent-Orchestrated Guides Surface
              </p>
              <h2 className="text-2xl font-bold text-slate-900">{classification.intent} layout for Guides & Lessons</h2>
              <p className="mt-2 max-w-3xl text-sm text-slate-600">
                The page is composing a tailored interface using live user signals from search, selected tabs, and access state instead of relying on a fixed path.
              </p>
            </div>

            <div className="grid min-w-[240px] grid-cols-2 gap-3">
              <div className="rounded-2xl bg-slate-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Free paths</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{freePaths.length}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Premium paths</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{premiumPaths.length}</p>
              </div>
            </div>
          </div>
        </div>
      ),
      primary: ({ emitSignal }) => {
        if (classification.intent === 'TRANSACTIONAL' && featuredFreePath) {
          return (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
                  <Zap className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">Action-ready workspace</h3>
                  <p className="text-sm text-slate-500">The user is ready to execute, so the engine is prioritizing direct actions.</p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <button
                  onClick={() => {
                    emitSignal({ type: 'submit', timestamp: Date.now(), metadata: { target: featuredFreePath.slug } });
                    onStartJourney(featuredFreePath);
                  }}
                  className="rounded-2xl bg-violet-600 px-4 py-4 text-left text-white hover:bg-violet-700"
                >
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Play className="h-4 w-4" />
                    Start {featuredFreePath.title}
                  </div>
                  <p className="mt-2 text-sm text-violet-100">Jump directly into the best-fit free journey path.</p>
                </button>

                <button
                  onClick={() => {
                    emitSignal({ type: 'cancel', timestamp: Date.now(), metadata: { reason: 'needs-premium-review' } });
                    onOpenSubscription();
                  }}
                  className="rounded-2xl border border-slate-300 px-4 py-4 text-left text-slate-800 hover:bg-slate-50"
                >
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Crown className="h-4 w-4" />
                    Review premium routes
                  </div>
                  <p className="mt-2 text-sm text-slate-500">Escalate into premium execution if the user needs deeper support.</p>
                </button>
              </div>
            </div>
          );
        }

        if (classification.intent === 'COMMERCIAL') {
          return (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                  <Crown className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">Commercial comparison layout</h3>
                  <p className="text-sm text-slate-500">This variant frames the trade-off between free exploration and premium depth.</p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Free access</p>
                  <p className="mt-2 text-sm text-slate-700">{freePaths.length} immediately available journeys</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Premium depth</p>
                  <p className="mt-2 text-sm text-slate-700">{premiumPaths.length} deeper guided tracks</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Access state</p>
                  <p className="mt-2 text-sm text-slate-700">{hasPremiumAccess ? 'Premium unlocked' : 'Free user mode'}</p>
                </div>
              </div>
            </div>
          );
        }

        if (classification.intent === 'NAVIGATIONAL') {
          return (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                  <Compass className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">Navigational shortcut layout</h3>
                  <p className="text-sm text-slate-500">The engine detects route-seeking behavior, so it foregrounds fast jumps.</p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                {[
                  { id: 'journeys' as const, label: 'Learning Paths', icon: BookOpen },
                  { id: 'lessons' as const, label: 'Quick Lessons', icon: Play },
                  { id: 'quick-courses' as const, label: 'Quick Wins', icon: Zap },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => onSwitchTab(item.id)}
                    className="rounded-2xl border border-slate-200 p-4 text-left hover:bg-slate-50"
                  >
                    <item.icon className="mb-2 h-5 w-5 text-slate-700" />
                    <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                  </button>
                ))}
              </div>
            </div>
          );
        }

        return (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                <BookOpen className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900">Informational layout</h3>
                <p className="text-sm text-slate-500">The engine is optimizing for understanding and guided discovery.</p>
              </div>
            </div>

            <div className="space-y-3">
              {readyFreePaths.slice(0, 3).map((path) => (
                <div key={path._id} className="rounded-2xl bg-slate-50 px-4 py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{path.title}</p>
                      <p className="mt-1 text-sm text-slate-500">Free path prioritized for early-stage knowledge seeking.</p>
                    </div>
                    <button
                      onClick={() => emitSignal({ type: 'ready-to-execute', timestamp: Date.now(), metadata: { pathId: path._id } })}
                      className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                    >
                      Ready to start
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      },
      secondary: () => (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">Live workspace signals</h3>
          <div className="mt-4 grid gap-3">
            <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700">Active tab: {activeTab}</div>
            <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700">Search query: {searchTerm || 'None'}</div>
            <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700">Available lessons: {lessons.length} · Quick wins: {quickCourses.length} · Paths in catalog: {journeyPaths.length}</div>
          </div>
        </div>
      ),
      support: () => <SystemReasoningPanel classification={classification} />,
    },
  });

  return (
    <GenerativeLayoutEngine
      query={query}
      signals={signals}
      userGoal="Help the user discover, compare, and start the right guide without forcing a fixed page journey."
      activeWorkspace="guides"
      fallbackMessage="The guides intent engine is uncertain, so it is falling back to a universal search surface."
      resolveVariant={resolveVariant}
      onIntentSuccess={(payload) => onIntentSuccess(`Intent success: ${payload.action}`)}
      onIntentFriction={(payload) => onIntentFriction(`Intent friction: ${payload.action}`)}
    />
  );
};

export default GuidesIntentWorkspace;